import { z } from 'zod';
import { asDomainRecord } from '../shared/domain-value.js';
import { shipmentCategoryForWeight } from '../shared/shipment-category.js';
import { standardShipmentDataDraftSchema, standardShipmentDraftSchema, heavyShipmentDraftSchema, unifiedShipmentDraftSchema, } from '../draft/unified-shipment-draft.schema.js';
import { receiverStreetIsRequired } from '../draft/shipment-input.schema.js';
import { persistedTimestampSchema, shipmentTimestampsSchema, } from '../shared/firestore-timestamp.schema.js';
import { shipmentPhaseProgressSchema, shipmentPhaseSchema, shipmentStatusDetailsSchema, shipmentStatusHistoryEntrySchema, } from '../shared/shipment-status.js';
import { dpdCarrierDataSchema, specialistAssignmentSchema } from '../carrier/carrier.schema.js';
import { paymentFlowSchema } from '../payment/payment.schema.js';
import { heavyShipmentLifecycleDataSchema } from '../heavy/heavy-lifecycle.schema.js';
import { webhookProcessingStatusSchema, webhookStageSchema, } from '../webhook/webhook.schema.js';
const lifecycleBase = {
    timestamps: shipmentTimestampsSchema,
    status: shipmentStatusDetailsSchema,
    phase: shipmentPhaseProgressSchema,
    /**
     * #1401 — le schéma de CRÉATION fige `currentPhase` sur le littéral du
     * premier état (`dpd_collection` / `heavy_processing`). Passé la création,
     * la phase AVANCE : la ré-élargir ici est obligatoire, sinon les schémas
     * lifecycle — qui héritent du schéma de brouillon par `.extend()` — jugeraient
     * invalide tout colis arrivé à l'entrepôt ou livré.
     *
     * `.optional()` est délibéré, et il n'affaiblit rien : ces schémas LISENT le
     * corpus existant (`unifiedShipmentLegacyReadSchema`,
     * `validateUnifiedShipment`), qui contient des documents antérieurs à #1401
     * dépourvus du champ — mesuré en production le 2026-08-24. Exiger le champ
     * ici ferait refuser des colis légitimes à la lecture sans rien corriger à
     * l'écriture. L'invariant « tout document NEUF porte sa phase racine » est
     * tenu là où il se décide : le writer de création et son schéma strict.
     */
    currentPhase: shipmentPhaseSchema.optional(),
    statusHistory: z.array(shipmentStatusHistoryEntrySchema),
};
const standardPricingLifecycleSchema = standardShipmentDataDraftSchema.shape.pricing
    .extend({
    calculatedAt: persistedTimestampSchema.optional(),
    breakdown: z
        .object({
        baseBefretPrice: z.number(),
        homeDeliveryCost: z.number(),
        emballageCost: z.number(),
        fragileCost: z.number(),
        totalSupplementsCost: z.number(),
    })
        .strict()
        .optional(),
})
    .strict();
const standardLifecycleDataSchema = standardShipmentDataDraftSchema
    .merge(dpdCarrierDataSchema)
    .extend({ pricing: standardPricingLifecycleSchema })
    .strict();
const heavyLifecycleDataSchema = heavyShipmentLifecycleDataSchema.merge(specialistAssignmentSchema);
/**
 * Document standard après création. Les ajouts asynchrones restent chacun sous
 * leur propriétaire : paiement dans `paymentFlow`, transport dans `standardData`
 * et exécution du webhook dans `webhookStatus`.
 */
export const standardShipmentLifecycleSchema = standardShipmentDraftSchema
    .extend({
    ...lifecycleBase,
    standardData: standardLifecycleDataSchema,
    paymentFlow: paymentFlowSchema.optional(),
    webhookStatus: webhookProcessingStatusSchema.optional(),
    metadata: standardShipmentDraftSchema.shape.metadata
        .extend({
        paymentContext: z
            .object({
            webhookReceived: z.boolean().optional(),
            dpdShipmentCreated: z.boolean().optional(),
            extensibleMetadataProcessed: z.boolean().optional(),
        })
            .strict()
            .optional(),
        recovery: z
            .object({
            nextRetryAt: persistedTimestampSchema,
            retryCount: z.number().int().nonnegative(),
            maxRetries: z.number().int().positive(),
            reason: z.string().min(1),
            automated: z.boolean(),
            manualRetry: z
                .object({ by: z.string(), at: persistedTimestampSchema })
                .strict()
                .optional(),
        })
            .strict()
            .optional(),
    })
        .strict(),
})
    .strict();
/** Document lourd enrichi par le backoffice et le transporteur spécialiste. */
export const heavyShipmentLifecycleSchema = heavyShipmentDraftSchema
    .extend({
    ...lifecycleBase,
    heavyData: heavyLifecycleDataSchema,
})
    .strict();
/**
 * Schéma d’écriture canonique du lifecycle B2C. Au-delà de la forme Zod, il
 * contrôle les invariants qui traversent plusieurs branches : poids/catégorie,
 * statut/phase, paiement/Payment Intent et création DPD/tracking.
 */
export const unifiedShipmentFinalSchema = z
    .discriminatedUnion('category', [standardShipmentLifecycleSchema, heavyShipmentLifecycleSchema])
    .superRefine((shipment, context) => {
    // #1706 — le lifecycle DÉRIVE des schémas de brouillon par `.extend()` : il a
    // donc hérité de la rue destinataire rendue optionnelle pour le retrait au
    // point relais. Sans ce contrôle, l'assouplissement ne se serait pas déplacé,
    // il aurait disparu — un envoi « livraison à domicile » sans adresse serait
    // devenu valide ICI, là où il était refusé avant le correctif. Même décision
    // que le brouillon (`receiverStreetIsRequired`), troisième point d'application.
    if (receiverStreetIsRequired(shipment.parcelInfo.deliveryMethod) &&
        !shipment.customerInfo.receiver.address.street) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerInfo', 'receiver', 'address', 'street'],
            message: 'Rue requise',
        });
    }
    const weight = shipment.parcelInfo.weight;
    // `parcelInfoSchema` porte déjà l'issue de poids. Le raffinement poursuit
    // les autres invariants sans appeler le catégoriseur jetant si elle existe.
    if (Number.isFinite(weight) &&
        weight > 0 &&
        shipment.category !== shipmentCategoryForWeight(weight)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['category'],
            message: 'Catégorie incohérente avec le poids',
        });
    }
    if (shipment.status.phase !== shipment.phase.current) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['phase', 'current'],
            message: 'Phase incohérente avec le statut',
        });
    }
    if (shipment.category === 'standard') {
        if (shipment.standardData.dpdShipmentId && !shipment.standardData.dpdTrackingNumber) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['standardData', 'dpdTrackingNumber'],
                message: 'Tracking DPD requis avec le shipment DPD',
            });
        }
        if (shipment.webhookStatus?.stages.dpd.status === 'success' &&
            !shipment.standardData.dpdShipmentId) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['standardData', 'dpdShipmentId'],
                message: 'Shipment DPD requis après succès webhook',
            });
        }
        if (shipment.paymentFlow?.paymentStatus === 'paid' && !shipment.paymentFlow.paymentIntentId) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['paymentFlow', 'paymentIntentId'],
                message: 'Payment Intent requis après paiement',
            });
        }
    }
    else {
        const workflow = shipment.heavyData.backofficeInfo.workflow;
        if (workflow?.payment?.paidAt && !workflow.payment.paymentIntentId) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['heavyData', 'backofficeInfo', 'workflow', 'payment', 'paymentIntentId'],
                message: 'Payment Intent lourd requis après paiement',
            });
        }
        const selectedCompany = workflow?.thirdPartyQuote?.selectedCompanyId;
        if (selectedCompany &&
            !workflow?.thirdPartyQuote?.companies.some((company) => company.id === selectedCompany)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['heavyData', 'backofficeInfo', 'workflow', 'thirdPartyQuote', 'selectedCompanyId'],
                message: 'Transporteur sélectionné absent des devis',
            });
        }
    }
});
const legacyStandardReadSchema = standardShipmentLifecycleSchema
    .extend({
    standardData: z.record(z.unknown()),
    paymentFlow: z.record(z.unknown()).optional(),
    webhookStatus: z.record(z.unknown()).optional(),
})
    .strict();
const legacyHeavyReadSchema = heavyShipmentLifecycleSchema
    .extend({
    heavyData: z.record(z.unknown()),
    paymentInfo: z.record(z.unknown()).optional(),
    specialistCarrier: z.record(z.unknown()).optional(),
})
    .strict();
/** Lecture des deux familles historiques ; aucune écriture ne doit utiliser ce schéma. */
export const unifiedShipmentLegacyReadSchema = z.discriminatedUnion('category', [
    legacyStandardReadSchema,
    legacyHeavyReadSchema,
]);
export const shipmentValidationResultSchema = z
    .object({ isValid: z.boolean(), errors: z.array(z.string()) })
    .strict();
export const legacyShipmentNormalizationResultSchema = z.discriminatedUnion('success', [
    z.object({ success: z.literal(true), shipment: unifiedShipmentFinalSchema }).strict(),
    z.object({ success: z.literal(false), errors: z.array(z.string()) }).strict(),
]);
function formatIssues(error) {
    return error.issues.map((issue) => `${issue.path.length ? issue.path.join('.') : 'shipment'}: ${issue.message}`);
}
/**
 * Compatibilité legacy en lecture uniquement : rassemble les différents noms
 * de jalons webhook et transforme les erreurs libres en erreur structurée.
 */
function normalizeLegacyStage(value) {
    const stage = asDomainRecord(value) ?? {};
    const rawError = stage.error;
    const errorRecord = asDomainRecord(rawError);
    const status = ['pending', 'success', 'failed', 'retrying', 'skipped'].includes(String(stage.status))
        ? stage.status
        : 'pending';
    return webhookStageSchema.parse({
        status,
        ...((stage.timestamp ?? stage.lastAttempt ?? stage.completedAt ?? stage.sentAt)
            ? { timestamp: stage.timestamp ?? stage.lastAttempt ?? stage.completedAt ?? stage.sentAt }
            : {}),
        ...(rawError
            ? {
                error: {
                    code: typeof errorRecord?.code === 'string' ? errorRecord.code : 'legacy_error',
                    message: typeof errorRecord?.message === 'string' ? errorRecord.message : String(rawError),
                    retriable: errorRecord?.retriable === true,
                },
            }
            : {}),
        ...(Number.isInteger(stage.retryCount) ? { retryCount: stage.retryCount } : {}),
        ...(stage.lastRetryAt ? { lastRetryAt: stage.lastRetryAt } : {}),
        ...(stage.nextRetryAt ? { nextRetryAt: stage.nextRetryAt } : {}),
    });
}
/**
 * Compatibilité legacy en lecture uniquement du suivi webhook monolithique.
 * Cette projection rend les anciens documents lisibles sans réintroduire leurs
 * emplacements redondants dans les nouvelles écritures.
 */
function normalizeLegacyWebhook(value) {
    const webhook = asDomainRecord(value);
    const stages = asDomainRecord(webhook?.stages);
    if (!webhook || !stages)
        return undefined;
    const notifications = asDomainRecord(stages.notifications);
    const collection = asDomainRecord(stages.collection);
    const normalized = {
        overall: webhook.overall ?? webhook.overallStatus ?? 'pending',
        actionRequired: webhook.actionRequired === true,
        contactSupport: webhook.contactSupport === true,
        lastUpdated: webhook.lastUpdated ?? webhook.updatedAt,
        createdAt: webhook.createdAt ?? webhook.lastUpdated ?? webhook.updatedAt,
        userMessage: asDomainRecord(webhook.userMessage) ?? { fr: '', en: '', nl: '' },
        stages: {
            // Ancien producteur à migrer : le webhook Stripe recopie le paiement ici ; cible `paymentFlow`.
            payment: normalizeLegacyStage(stages.payment),
            // Ancien producteur à migrer : le webhook Stripe recopie DPD ici ; cible `standardData`.
            dpd: normalizeLegacyStage(stages.dpd),
            ...(collection
                ? {
                    collection: {
                        ...normalizeLegacyStage(collection),
                        ...(Array.isArray(collection.rejectedDates)
                            ? { rejectedDates: collection.rejectedDates }
                            : {}),
                        ...(typeof collection.confirmedDate === 'string'
                            ? { confirmedDate: collection.confirmedDate }
                            : {}),
                        ...(typeof collection.needsRebooking === 'boolean'
                            ? { needsRebooking: collection.needsRebooking }
                            : {}),
                    },
                }
                : {}),
            // Ancien producteur à migrer : le webhook Stripe recopie le label ici ; cible `standardData`.
            label: normalizeLegacyStage(stages.label),
            notifications: {
                email: normalizeLegacyStage(notifications?.email),
                ...(notifications?.sms ? { sms: normalizeLegacyStage(notifications.sms) } : {}),
            },
        },
    };
    const result = webhookProcessingStatusSchema.safeParse(normalized);
    return result.success ? result.data : undefined;
}
/**
 * Compatibilité legacy en lecture uniquement : convertit les emplacements
 * historiques connus puis applique le schéma canonique. Une valeur déjà
 * canonique gagne toujours sur son ancien alias et la sortie ne réémet pas les
 * branches obsolètes.
 */
export function normalizeLegacyUnifiedShipment(shipment) {
    const legacy = unifiedShipmentLegacyReadSchema.safeParse(shipment);
    if (!legacy.success)
        return { success: false, errors: formatIssues(legacy.error) };
    const source = legacy.data;
    const normalized = { ...source };
    if (source.category === 'standard') {
        const standardData = { ...(asDomainRecord(source.standardData) ?? {}) };
        const paymentFlow = { ...(asDomainRecord(source.paymentFlow) ?? {}) };
        const webhook = asDomainRecord(source.webhookStatus);
        const webhookStages = asDomainRecord(webhook?.stages);
        const webhookPayment = asDomainRecord(webhookStages?.payment);
        const webhookDpd = asDomainRecord(webhookStages?.dpd);
        const webhookLabel = asDomainRecord(webhookStages?.label);
        const dpdIntegration = asDomainRecord(standardData.dpdIntegration);
        // Ancien chemin Stripe encore lu par Angular : `standardData.*` → `paymentFlow`.
        if (paymentFlow.stripeSessionId == null && standardData.stripeSessionId != null)
            paymentFlow.stripeSessionId = standardData.stripeSessionId;
        if (paymentFlow.paymentIntentId == null && standardData.paymentIntentId != null)
            paymentFlow.paymentIntentId = standardData.paymentIntentId;
        // Ancien chemin produit par le webhook Stripe : détails du stage paiement → `paymentFlow`.
        if (paymentFlow.paymentIntentId == null && webhookPayment?.paymentIntentId != null)
            paymentFlow.paymentIntentId = webhookPayment.paymentIntentId;
        if (paymentFlow.paymentAmount == null && webhookPayment?.amount != null)
            paymentFlow.paymentAmount = webhookPayment.amount;
        if (paymentFlow.paymentCurrency == null && webhookPayment?.currency != null)
            paymentFlow.paymentCurrency = webhookPayment.currency;
        if (paymentFlow.paymentStatus == null && webhookPayment?.paymentStatus === 'success')
            paymentFlow.paymentStatus = 'paid';
        delete standardData.stripeSessionId;
        delete standardData.paymentIntentId;
        // Ancien chemin lu par certains écrans Angular : `standardData.befretTrackingNumber` → `trackingNumber`.
        if (normalized.trackingNumber == null && standardData.befretTrackingNumber != null)
            normalized.trackingNumber = standardData.befretTrackingNumber;
        delete standardData.befretTrackingNumber;
        // Anciens chemins du webhook DPD : `standardData.dpdIntegration` et son stage → `standardData`.
        if (standardData.dpdShipmentId == null)
            standardData.dpdShipmentId = dpdIntegration?.shipmentId ?? webhookDpd?.dpdShipmentId;
        if (standardData.dpdTrackingNumber == null)
            standardData.dpdTrackingNumber =
                dpdIntegration?.trackingNumber ?? webhookDpd?.dpdTrackingNumber;
        if (standardData.labelUrl == null)
            standardData.labelUrl = dpdIntegration?.labelUrl ?? webhookLabel?.labelUrl;
        if (standardData.labelPdf == null)
            standardData.labelPdf = dpdIntegration?.labelPdf ?? webhookLabel?.labelPdf;
        if (standardData.trackingUrl == null)
            standardData.trackingUrl = dpdIntegration?.trackingUrl;
        delete standardData.dpdIntegration;
        for (const key of [
            'dpdShipmentId',
            'dpdTrackingNumber',
            'labelUrl',
            'labelPdf',
            'trackingUrl',
        ]) {
            if (standardData[key] == null || standardData[key] === '')
                delete standardData[key];
        }
        normalized.standardData = standardData;
        if (Object.keys(paymentFlow).length)
            normalized.paymentFlow = paymentFlow;
        const webhookStatus = normalizeLegacyWebhook(source.webhookStatus);
        if (webhookStatus)
            normalized.webhookStatus = webhookStatus;
    }
    else {
        const heavyData = { ...(asDomainRecord(source.heavyData) ?? {}) };
        const specialist = asDomainRecord(source.specialistCarrier);
        // Ancien chemin du modèle Angular lourd : `specialistCarrier.*` → `heavyData.*`.
        if (heavyData.specialistId == null && specialist?.specialistId != null)
            heavyData.specialistId = specialist.specialistId;
        if (heavyData.transportPartner == null && specialist?.transportPartner != null)
            heavyData.transportPartner = specialist.transportPartner;
        if (heavyData.collectionScheduledAt == null && specialist?.collectionScheduledAt != null)
            heavyData.collectionScheduledAt = specialist.collectionScheduledAt;
        if (heavyData.collectionCompletedAt == null && specialist?.collectionCompletedAt != null)
            heavyData.collectionCompletedAt = specialist.collectionCompletedAt;
        const backofficeInfo = { ...(asDomainRecord(heavyData.backofficeInfo) ?? {}) };
        // Ancien chemin des écrans lourds : `heavyData.workflow` → `heavyData.backofficeInfo.workflow`.
        if (backofficeInfo.workflow == null && heavyData.workflow != null)
            backofficeInfo.workflow = heavyData.workflow;
        delete heavyData.workflow;
        const workflow = { ...(asDomainRecord(backofficeInfo.workflow) ?? {}) };
        const quotation = asDomainRecord(heavyData.quotationData);
        const quoteDetails = asDomainRecord(heavyData.quoteDetails);
        // Anciens lecteurs marketing/Angular : `quotationData` et `quoteDetails` → `workflow.clientQuote`.
        if (workflow.clientQuote == null && (quotation || quoteDetails)) {
            workflow.clientQuote = {
                quoteAmount: quotation?.amount ?? quoteDetails?.finalPrice ?? 0,
                quoteCurrency: quotation?.currency ?? quoteDetails?.currency ?? 'EUR',
                ...(quotation?.quotationPdf ? { quotePdfUrl: quotation.quotationPdf } : {}),
                ...(quotation?.validUntil ? { validUntil: quotation.validUntil } : {}),
                ...(quotation?.sentAt ? { quoteSentAt: quotation.sentAt } : {}),
                ...(quotation?.respondedAt ? { clientResponseDate: quotation.respondedAt } : {}),
            };
        }
        delete heavyData.quotationData;
        delete heavyData.quoteDetails;
        // Ancien producteur du paiement lourd : `paymentInfo` → `heavyData.backofficeInfo.workflow.payment`.
        const legacyPayment = asDomainRecord(source.paymentInfo);
        if (workflow.payment == null && legacyPayment) {
            workflow.payment = {
                ...(legacyPayment.paidAt ? { paidAt: legacyPayment.paidAt } : {}),
                ...(legacyPayment.amount != null ? { amount: legacyPayment.amount } : {}),
                ...(legacyPayment.currency ? { currency: legacyPayment.currency } : {}),
                ...(legacyPayment.paymentIntentId
                    ? { paymentIntentId: legacyPayment.paymentIntentId }
                    : {}),
            };
        }
        if (Object.keys(workflow).length)
            backofficeInfo.workflow = workflow;
        heavyData.backofficeInfo = backofficeInfo;
        normalized.heavyData = heavyData;
        delete normalized.paymentInfo;
        delete normalized.specialistCarrier;
    }
    const result = unifiedShipmentFinalSchema.safeParse(normalized);
    return result.success
        ? { success: true, shipment: result.data }
        : { success: false, errors: formatIssues(result.error) };
}
export function validateUnifiedShipment(shipment, mode = 'final') {
    const result = (mode === 'initial' ? unifiedShipmentDraftSchema : unifiedShipmentFinalSchema).safeParse(shipment);
    return result.success
        ? { isValid: true, errors: [] }
        : { isValid: false, errors: formatIssues(result.error) };
}
